// HOW A SPLAT MESH IS DRAWN — the decisions that must hold for EVERY splat in
// the app, and the order it is drawn in.
//
// WHY THIS IS ONE MODULE. There are two places a `GaussianSplat` is built: the
// avatar's (with the room folded into the same mesh, `ThreeSplatBridge`) and a
// GLB character's room (`ThreeEnvironmentSplat`). They had the same settings
// written out twice, and the copies had DRIFTED APART in two ways, both silent
// and both measured in CLAUDE.md as settled decisions:
//
//   - `sortRadial` was set by the bridge ONLY, and it is a CLASS static. So a
//     GLB character's room — mounted with no bridge in the page — sorted on
//     three's planar `-viewZ` instead. The two diverge off-axis, and the
//     measurement is not subtle: far splats paint over near ones and thin dark
//     features wash out, +12.8 and +14.7 levels too bright against a reference
//     that was right to within one level.
//   - the STABLE SORT was never wired on the room's path at all. Its component
//     carried the code to drive one — the `sortRef` reads, the buffer lookups,
//     the comment explaining the ordering guarantee — but nothing ever assigned
//     `sortRef.current`, so every read fell through and the mesh quietly kept
//     three's binned `CountingSort`, whose order within a bin is thread-arrival
//     order (two runs over identical splats disagree in 96.8% of slots). That is
//     the flicker the stable radix exists to remove.
//
// Neither was reachable from the other path's tests, and neither is visible in
// the file that was wrong — which is the argument for one module rather than two
// careful copies.

/**
 * The settings every splat mesh in this app is drawn with. Class statics, so
 * this must be called before a mesh is built — by whoever builds one.
 *
 * KERNEL CUTOFF 2, three's own, not sparkjs's 2.83. 2.83 is measurably better —
 * three truncates each Gaussian where its alpha is still exp(-2) = 13.5% of peak
 * against sparkjs's 1.8%, worth about a sixth of one level in 255 — and it costs
 * DOUBLE, because drawn area goes as the cutoff squared and on the avatar's path
 * the room is in the same mesh. Measured on isaac-v8 at 1600x1000: 97.7 ms a
 * frame at 2.83 against 35.0 at 2. It is also what the AVATAR wants: its
 * decoders were fit against a 2-sigma rasterizer.
 *
 * SORT BY RADIAL DISTANCE, sparkjs's convention, not three's planar `-viewZ`.
 * See the header for what planar costs. It is one line in the sort key and the
 * depth RANGE moves with it (`_updateSortUniforms`), or the bins cover the wrong
 * interval.
 */
export function configureSplatClass(GaussianSplat) {
  if (!GaussianSplat) return
  GaussianSplat.kernelCutoff = 2
  GaussianSplat.sortRadial = true
}

/** The GPUBuffer behind a three attribute, for the renderer that owns it. */
function bufferOf(renderer, attribute) {
  if (!attribute) return null
  try { return renderer?.backend?.get?.(attribute)?.buffer ?? null } catch { return null }
}

/**
 * The stable, deterministic draw order for one mesh.
 *
 * THE CENTRES ARE NOT A GEOMETRY ATTRIBUTE. `GaussianSplat` moves them into a
 * storage buffer at construction, reachable only through the node that reads
 * them — asking the geometry returns null, the sort silently never runs, and a
 * mesh with `autoSort` already off then draws in arbitrary order (mostly black
 * with white blotches). Both lookups live here so neither caller can get that
 * wrong on its own.
 */
export class SplatOrder {
  /** @param {{sort: (centre, count, cameraPos, order) => boolean, dispose?: () => void}} sorter
   *   the radix itself — INJECTED rather than constructed here, so this module
   *   imports no WGSL and node can drive the part that actually broke: the
   *   lookups and the handover. Both callers pass `new ThreeStableSort(device)`;
   *   which sort it is was never the thing that drifted. */
  constructor(sorter, warn = console.warn) {
    this.sort = sorter
    /** Whether an order has EVER been submitted for this mesh. Read by `run`,
     *  and it is the one thing that makes a later failure reportable. */
    this.ran = false
    this._warn = warn
    this._warnedStopped = false
  }

  /**
   * Submit the order for the frame that is ABOUT to be drawn. Same queue as the
   * raster, so submission order is the ordering guarantee.
   *
   * @returns {boolean} whether an order was actually submitted — the caller can
   *   use it to hand over from three's own sort only once ours has worked.
   */
  run(renderer, splat, cameraPosition, count = null) {
    const centre = bufferOf(renderer, splat?._buffers?.centerRead?.value ?? null)
    const order = bufferOf(renderer, splat?._sort?.orderAttribute)
    const n = count ?? splat?._buffers?.count ?? 0
    const ok = !!(centre && order && n > 0) && this.sort.sort(centre, n, cameraPosition, order)
    // ONCE OURS HAS RUN, ITS FAILING AGAIN IS SILENT AND TOTAL. `adoptStableOrder`
    // switches three's own sort off on the first success, so a later failure —
    // a rebuilt mesh whose buffers the renderer has not backed yet, a disposed
    // device — leaves the mesh with NO order at all, which renders as noise
    // rather than as a worse order. Said once: it is a per-frame path.
    if (this.ran && !ok && !this._warnedStopped) {
      this._warnedStopped = true
      this._warn('[splat-order] the stable sort stopped submitting after it had been running — '
        + "three's own sort is already off, so this mesh is drawing UNSORTED")
    }
    this.ran = this.ran || ok
    return ok
  }

  /** A NEW MESH is a new order: the mesh is rebuilt with `autoSort: true`, so
   *  three is sorting it again and a no-run is not yet a defect. Called by
   *  whoever rebuilds the mesh; the sorter itself is kept (it belongs to the
   *  device, not the mesh). */
  reset() {
    this.ran = false
    this._warnedStopped = false
  }

  dispose() {
    try { this.sort?.dispose?.() } catch { /* best effort */ }
  }
}

/**
 * HAND OVER FROM three's SORT ONLY ONCE OURS HAS RUN.
 *
 * `autoSort` cannot simply be switched off at construction: if the stable sort
 * never manages to submit — no device, a renderer that owns no buffer for the
 * attribute yet — the mesh is left with NO order at all, which does not degrade,
 * it renders as noise. Starting with three's sort and standing it down on the
 * first successful submission means the mesh is always sorted by someone, and
 * the better sort takes over as soon as it can.
 */
export function adoptStableOrder(splat, submitted) {
  if (!submitted || !splat || splat.autoSort === false) return false
  splat.autoSort = false
  return true
}
